import type { User } from '@supabase/supabase-js';

import { supabase } from '@/lib/supabase';
import { isSessionReconnecting } from '@/lib/refreshHealth';
import { ReconnectingError } from '@/services/errors';

/**
 * The signed-in user, read from the local session (JOSH-89).
 *
 * `auth.getUser()` sends the access token to /auth/v1/user. During a refresh
 * cooldown auth-js serves the stored, possibly expired session, so that call
 * fails and reads as "signed out" while the session is only reconnecting (the
 * guard leaves /auth/v1 routes untouched). Callers that only need the user id
 * (to find their pessoa, for attribution) read it here, with no network call.
 * RLS still validates the JWT on the data request that follows.
 *
 * Returns null when nobody is signed in. Throws a `ReconnectingError` when no
 * session can be read while reconnecting.
 */
export async function getSessionUser(): Promise<User | null> {
  const { data } = await supabase.auth.getSession();
  const user = data.session?.user ?? null;
  if (!user && isSessionReconnecting()) throw new ReconnectingError();
  return user;
}
